import React, { useState, useEffect, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Dropdown from 'react-bootstrap/Dropdown'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faDatabase, faMagic, faEye } from '@fortawesome/free-solid-svg-icons'
import { useReport } from 'api/hooks/use-report-builder'
import { useReportDatasets } from 'api/hooks/use-report-datasets'
import { useReportGridRows } from 'api/hooks/use-report-grid-rows'
import { useCreateReportUser } from 'api/hooks/use-report-user'
import { useAssistantConversation } from 'api/hooks/use-report-assistant'
import UserContext from 'context/user-context'
import ShareButton from 'shared/buttons/share-button'
import CenteredSpinner from 'shared/centered-spinner'
import CardError from 'shared/card-error'
import ReportActionsMenu from './report-actions-menu'
import ReportLockBadge from './report-lock-badge'
import DatasetModal from './dataset-modal'
import AssistantDrawer from './assistant-drawer'
import ReportUserRow from './user-row'
import ReportGrid from './grid'
import ChildReportBanner from './child-report-banner'
import ParentReportBanner from './parent-report-banner'
import SplitReportModal from './split-report-modal'
import Page from 'layout/page'
import { useClearOnOutsideClick } from 'shared/use-clear-on-outside-click'
import { useFolderContext, useFolderReturnPath, withFolderContext } from 'dashboard/folders/folder-context'

function ReportShow() {
  const { id } = useParams()
  const navigate = useNavigate()
  // Set when the report was opened from a folder on the index. Back goes to that folder, however
  // deeply it is nested, rather than to the top of the index.
  const folderId = useFolderContext()
  const backPath = useFolderReturnPath()
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [showDatasetModal, setShowDatasetModal] = useState(false)
  // The assistant is the primary way a report gets built, so it opens with the page. Closing it is
  // remembered per report — reopening on every visit after the user dismissed it would be a nag,
  // not a default.
  const [showAssistant, setShowAssistant] = useState(() => {
    try { return window.localStorage.getItem(`assistant-closed:${id}`) !== '1' } catch { return true }
  })
  const [showSplit, setShowSplit] = useState(false)

  // The active assistant conversation is tracked here (not inside the drawer) and persisted per report,
  // so the agent keeps working — and reappears — even after the drawer is closed or the page reloaded.
  const [conversationId, setConversationId] = useState(() => {
    try { return window.localStorage.getItem(`assistant-convo:${id}`) || null } catch { return null }
  })
  useEffect(() => {
    try {
      if (conversationId) window.localStorage.setItem(`assistant-convo:${id}`, conversationId)
      else window.localStorage.removeItem(`assistant-convo:${id}`)
    } catch { /* ignore storage errors */ }
  }, [conversationId, id])

  useEffect(() => {
    try {
      window.localStorage.setItem(`assistant-closed:${id}`, showAssistant ? '0' : '1')
    } catch { /* ignore storage errors */ }
  }, [id, showAssistant])

  // Poll the active conversation's status even while the drawer is closed, so the launcher button can
  // show a spinner when the agent is still working. Shares react-query's key with the drawer's poll.
  const { data: activeConversation } = useAssistantConversation({
    reportId: id,
    conversationId,
    options: { refetchInterval: (data) => (data?.status === 'running' ? 1200 : false) }
  })
  const assistantRunning = activeConversation?.status === 'running'

  const [selectedChartId, setSelectedChartId] = useState(null)
  const [selectedRowId, setSelectedRowId] = useState(null)
  const [selectedEmptySlotRaw, setSelectedEmptySlot] = useState(null)
  const clearFocus = () => { setSelectedChartId(null); setSelectedRowId(null); setSelectedEmptySlot(null) }
  useClearOnOutsideClick(Boolean(selectedChartId || selectedRowId || selectedEmptySlotRaw), clearFocus)

  const handleSelectChart = (chartId) => {
    setSelectedChartId(chartId)
    if (chartId) {
      setSelectedRowId(null)
      setSelectedEmptySlot(null)
    }
  }

  const user = useContext(UserContext)
  const {
    data: report,
    isSuccess,
    isError: isReportError,
    isFetching: isReportFetching,
    refetch: refetchReport
  } = useReport({ id })
  const { data: datasets = [] } = useReportDatasets({ reportId: id })
  const {
    data: gridRows = [],
    isLoading: isGridRowsLoading,
    isError: isGridRowsError,
    isFetching: isGridRowsFetching,
    refetch: refetchGridRows
  } = useReportGridRows({ reportId: id, options: { enabled: !!id, refetchOnWindowFocus: false } })

  const isUserCreator = report?.user_id === user.id
  // A child takes its edit access from the template's share list, not its own (editor_via_parent):
  // sharing a report shares every slice it was split into, at the same access level.
  const isReportEditor = (report?.shared_users?.find((ru) => ru.user_id === user.id)?.is_editor ?? false) ||
    !!report?.editor_via_parent
  // A child report renders its parent's layout — there is nothing of its own to edit, so it is
  // read-only for everyone, its owner included.
  const isChild = !!report?.is_child
  // The owner's lock freezes the report for everyone, the owner included, until they unlock it.
  const isLocked = !!report?.locked
  const canEdit = (isUserCreator || isReportEditor) && !isChild && !isLocked
  // The assistant + NL dataset creation call AI endpoints gated on the municipality's
  // ai_search_enabled setting, so only surface them when that's actually on.
  const aiEnabled = user?.municipality?.setting?.ai_search_enabled ?? false
  // Selecting a chart (yellow outline) reveals its Actions menu (edit / branding / move /
  // delete) AND hands it to the assistant as focus context. That editing affordance should be
  // available whenever the report is editable — not only while the assistant is open — so gate
  // selection on edit access rather than on the drawer being open.
  const selectionEnabled = canEdit && !isPreviewMode
  const assistantActive = canEdit && aiEnabled && showAssistant

  const selectedChart = gridRows.flatMap(r => r.report_charts ?? []).find(c => c.id === selectedChartId) ?? null

  // Derive from live gridRows so a deleted/filled slot clears itself, like selectedChart above.
  const selectedEmptySlot = (() => {
    if (!selectedEmptySlotRaw) return null
    const row = gridRows.find(r => r.id === selectedEmptySlotRaw.rowId)
    if (!row || selectedEmptySlotRaw.position >= row.column_count) return null
    const occupied = (row.report_charts ?? []).some(c => c.grid_column_position === selectedEmptySlotRaw.position)
    return occupied ? null : selectedEmptySlotRaw
  })()

  const navTitle = isSuccess ? (
    <span className="d-flex align-items-center gap-2">
      <Button
        variant="light"
        size="sm"
        className="border"
        onClick={() => navigate(backPath)}
        title="Back to Views"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
      </Button>
      {report.name}
      <ReportLockBadge report={report} />
    </span>
  ) : ''

  const navActions = isSuccess ? (
    <div className="d-flex align-items-center gap-2">
      {canEdit && (
        <Button
          variant="light"
          className="border d-flex align-items-center gap-2"
          onClick={() => setShowDatasetModal(true)}
        >
          <FontAwesomeIcon icon={faDatabase} />
          Data sources
          <Badge bg="primary" pill>
            {datasets.length}
          </Badge>
        </Button>
      )}
      <ShareButton
        resource={report}
        resourceName={report?.name}
        useCreateMutation={useCreateReportUser}
        createParams={{ report_id: report?.id }}
        queryKeys={[`reports/${report?.id}`, 'reports-builder']}
        UserRowComponent={ReportUserRow}
        userRowProps={(u) => ({ reportUser: u, editable: isUserCreator, reportId: report?.id })}
        buttonText="Share"
        size={null}
        triggerClassName="border"
      />
      <ReportActionsMenu
        report={report}
        align="end"
        toggleLabel="Actions"
        onDeleted={() => navigate(backPath)}
        onDuplicated={(newReport) =>
          newReport?.id && navigate(withFolderContext(`/reports/${newReport.id}`, folderId))}
      >
        <Dropdown.Item onClick={() => setIsPreviewMode((v) => !v)}>
          <FontAwesomeIcon icon={faEye} fixedWidth className="me-2 text-muted" />
          {isPreviewMode ? 'Exit preview' : 'Preview'}
        </Dropdown.Item>
      </ReportActionsMenu>
    </div>
  ) : null

  return (
    <Page title={navTitle} pageTitle={report?.name || 'Report'} navActions={navActions}>
      {isReportError ? (
        <CardError
          title="Couldn't load this report"
          onRetry={() => refetchReport()}
          isRetrying={isReportFetching}
        />
      ) : !isSuccess ? (
        <CenteredSpinner />
      ) : (
        <>
          <DatasetModal
            report={report}
            canEdit={canEdit}
            reportId={report.id}
            folderId={report.folder_id}
            show={showDatasetModal}
            onHide={() => setShowDatasetModal(false)}
          />

          <SplitReportModal
            report={report}
            canEdit={canEdit}
            show={showSplit}
            onHide={() => setShowSplit(false)}
          />

          {/* The banners sit inside the reflowed column, not above it: the assistant is docked as
              a fixed right sidebar, so anything outside this margin gets covered by it. */}
          <div
            style={{
              // While the assistant is docked as a right sidebar, reserve its width (plus its
              // right gutter) so the report reflows to the left instead of being covered by it.
              marginRight: assistantActive ? 424 : 0,
              transition: 'margin-right 0.25s ease-out'
            }}
          >
            {isChild && <ChildReportBanner report={report} />}
            {!isChild && (
              <ParentReportBanner
                report={report}
                canEdit={canEdit}
                onManage={() => setShowSplit(true)}
              />
            )}

            <div className="d-flex align-items-start gap-4">
              <div
                className="bg-white shadow-sm rounded-2 p-4"
                style={{ flex: '1 1 0', minWidth: 0, minHeight: '600px' }}
              >
                <ReportGrid
                  reportId={report.id}
                  gridRows={gridRows}
                  datasets={datasets}
                  // Null unless the report declares a taxonomy, which heads every block.
                  reportGroupingId={report.account_grouping_id}
                  canEdit={canEdit && !isPreviewMode}
                  isPreviewMode={isPreviewMode}
                  selectedChartId={selectionEnabled ? selectedChartId : null}
                  onSelectChart={selectionEnabled ? handleSelectChart : null}
                  selectedRowId={selectedRowId}
                  onSelectRow={setSelectedRowId}
                  selectedEmptySlot={selectedEmptySlot}
                  onSelectEmptySlot={setSelectedEmptySlot}
                  isLoading={isGridRowsLoading}
                  isError={isGridRowsError}
                  onRetry={() => refetchGridRows()}
                  isRetrying={isGridRowsFetching}
                />
              </div>
            </div>
          </div>

          {canEdit && aiEnabled && showAssistant && (
            <AssistantDrawer
              reportId={report.id}
              reportName={report.name}
              datasetCount={datasets.length}
              conversationId={conversationId}
              setConversationId={setConversationId}
              selectedChart={selectedChart}
              selectedEmptySlot={selectedEmptySlot}
              onClearFocus={clearFocus}
              onClose={() => setShowAssistant(false)}
            />
          )}
          {canEdit && aiEnabled && !showAssistant && (
            <Button
              variant="primary"
              className="rounded-circle shadow d-flex align-items-center justify-content-center"
              style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', width: 56, height: 56, zIndex: 1040, fontSize: '1.25rem' }}
              onClick={() => setShowAssistant(true)}
              title={assistantRunning ? 'Assistant is working…' : 'Assistant'}
              aria-label="Open assistant"
            >
              {assistantRunning
                ? <Spinner animation="border" size="sm" />
                : <FontAwesomeIcon icon={faMagic} />}
            </Button>
          )}
        </>
      )}
    </Page>
  )
}

export default ReportShow
